import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import EditPoint from "../../Edit/EditPoint";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Container, Form } from "react-bootstrap";
import BackButton from "../../../../components/UI_Kit/BackButton/BackButton";
import "../../styles.css";
import ViewpointFlow from "./ViewpointFlow"; // Import the ViewpointFlow component
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Import api at the top of the file
import api from "../../../../services/api";

// Helper function to convert File objects or Blob URLs to Base64
const urlToBase64 = async (urlOrFile) => {
  if (urlOrFile instanceof File) {
    // Handle File object
    // Check if it's a HEIC file
    if (urlOrFile.name && urlOrFile.name.toLowerCase().endsWith(".heic")) {
      // For HEIC files, we still convert to base64 but need to handle metadata differently
      try {
        // If heic-convert is available, you could use it here to convert to JPEG
        // For now, we'll just convert to base64 as is
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(urlOrFile);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      } catch (error) {
        console.error("Error processing HEIC file:", error);
        return null;
      }
    }
    // Handle other file types normally
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(urlOrFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  } else if (typeof urlOrFile === "string" && urlOrFile.startsWith("blob:")) {
    // Handle Blob URL
    try {
      const response = await fetch(urlOrFile);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error fetching or converting blob URL:", error);
      return null; // Indicate failure
    }
  } else if (
    typeof urlOrFile === "string" &&
    urlOrFile.startsWith("data:image")
  ) {
    // Handle existing Base64 data URL
    return Promise.resolve(urlOrFile);
  } else {
    // Handle other cases (e.g., regular server URLs, null, undefined)
    // For regular URLs, we might not want/need to convert them here
    console.warn(
      "Source is not a File, Blob URL, or Data URL. Cannot convert to Base64:",
      urlOrFile
    );
    return Promise.resolve(null); // Resolve with null if conversion isn't applicable/possible
  }
};

const PointsInfo = () => {

  const { id } = useParams();

  // ========== STATE MANAGEMENT ==========
  const [showViewpointFlow, setShowViewpointFlow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotation, setQuotation] = useState(null);

  const [points, setPoints] = useState([]);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("access_token");
        const response = await api.get(`/quotation/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const fetchedQuotation = response.data.quotation;
        setQuotation(fetchedQuotation);

        const pricingMap = {
          purchase: { leasing: false, achat: true },
          leasing: { leasing: true, achat: false },
          purchase_and_leasing: { leasing: true, achat: true },
        };

        const { leasing = false, achat = false } =
          pricingMap[fetchedQuotation?.pricing] || {};

        setPoints(response.data.quotation.points || []);
      } catch (err) {
        setError("Failed to fetch quotation data");
        toast.error("Failed to fetch quotation data");
        console.error("Error fetching quotation:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuotation();
    } else {
      setError("Quotation ID is missing.");
      setLoading(false);
    }
  }, [id]);
  const [isDraftClicked, setIsDraftClicked] = useState(false);
  const [isQuitClicked, setIsQuitClicked] = useState(false);
  const [quickPointName, setQuickPointName] = useState("");
  const [collectedPoints, setCollectedPoints] = useState([]);
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate(); // Add this line to use navigation
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this state
  const [showBackModal, setShowBackModal] = useState(false); // Ajout de l'état pour la modale

  const draftClickedRef = useRef(false);
  const QuitClickedRef = useRef(false);

  const handleDraftClick = () => {
    draftClickedRef.current = true; // Set ref value immediately
    QuitClickedRef.current = false;
    console.log("Draft clicked:", draftClickedRef.current); // Will log true right away
    console.log("Quit clicked:", QuitClickedRef.current); // Will log true right away
    setIsDraftClicked(true);
  };

  const handleQuitClick = () => {
    QuitClickedRef.current = true; // Set ref value immediately
    draftClickedRef.current = false;
    console.log("Quit clicked:", QuitClickedRef.current);
    console.log("Draft clicked:", draftClickedRef.current);
    setIsQuitClicked(true);
  };

  // Update the handleSubmit function
  const parseCoordinates = (coordString) => {
    if (!coordString) return { lat: null, lon: null };

    const dmsRegex =
      /(-?\d+(\.\d+)?)\s?°?\s?([NS])?,\s?(-?\d+(\.\d+)?)\s?°?\s?([EW])?/i;
    const simpleRegex = /(-?\d+(\.\d+)?),\s?(-?\d+(\.\d+)?)/;

    let match = coordString.match(dmsRegex);
    if (match) {
      let lat = parseFloat(match[1]);
      let lon = parseFloat(match[4]);
      const latDir = match[3];
      const lonDir = match[6];

      if (!isNaN(lat) && !isNaN(lon)) {
        if (latDir?.toUpperCase() === "S") lat = -Math.abs(lat);
        else lat = Math.abs(lat);
        if (lonDir?.toUpperCase() === "W") lon = -Math.abs(lon);
        else lon = Math.abs(lon);
        return { lat, lon };
      }
    }

    match = coordString.match(simpleRegex);
    if (match) {
      const lat = parseFloat(match[1]);
      const lon = parseFloat(match[3]);
      if (!isNaN(lat) && !isNaN(lon)) return { lat, lon };
    }

    console.warn("Invalid GPS coordinate format:", coordString);
    return { lat: null, lon: null };
  };

  const processPhoto = async (photo) => {
    if (!photo) return null;

    let base64 = null;
    let parsedLat = null;
    let parsedLon = null;
    let exifLat = null;
    let exifLon = null;

    const source = photo.file || photo.url;
    const isHeic = source instanceof File && source.name?.toLowerCase().endsWith(".heic");

    if (source) base64 = await urlToBase64(source);
    if (isHeic && photo.exifData) {
      exifLat = photo.exifData.latitude;
      exifLon = photo.exifData.longitude;
    }

    if (typeof photo.location === "string") {
      const { lat, lon } = parseCoordinates(photo.location);
      parsedLat = lat;
      parsedLon = lon;
    } else if (photo.location?.latitude && photo.location?.longitude) {
      exifLat = photo.location.latitude;
      exifLon = photo.location.longitude;
    }

    parsedLat ??= photo.latitude ?? null;
    parsedLon ??= photo.longitude ?? null;

    if (!base64 && !parsedLat && !parsedLon && !exifLat && !exifLon) return null;

    return { base64, parsedLat, parsedLon, exifLat, exifLon };
  };

  const buildLocalisations = (manualCoords, photosData) => {
    const apiLocalisations = [];

    if (manualCoords.lat !== null && manualCoords.lon !== null) {
      apiLocalisations.push({
        latitude: manualCoords.lat,
        longitude: manualCoords.lon,
        image_path: photosData[0]?.base64 || "",
      });

      photosData.slice(1).forEach((photo) =>
        apiLocalisations.push({
          latitude: 0,
          longitude: 0,
          image_path: photo.base64 || "",
        })
      );
    } else if (photosData.length > 0) {
      photosData.forEach((photo) =>
        apiLocalisations.push({
          latitude: photo.parsedLat ?? photo.exifLat ?? 0,
          longitude: photo.parsedLon ?? photo.exifLon ?? 0,
          image_path: photo.base64 || "",
        })
      );
    } else {
      apiLocalisations.push({ latitude: 0, longitude: 0, image_path: "" });
    }

    return apiLocalisations;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const quotationId = window.location.pathname.split("/").pop();
      const token = localStorage.getItem("access_token");
      if (!quotationId || !token) throw new Error("Missing quotation ID or token.");

      for (const point of collectedPoints) {
        const manualCoords = parseCoordinates(point.gpsCoordinatesString || "");

        const photosData = point.photos
          ? (await Promise.all(point.photos.map(processPhoto))).filter(Boolean)
          : [];

        const apiLocalisations = buildLocalisations(manualCoords, photosData);

        const pointPayload = {
          title: point.pointName,
          type: point.pointType,
          support_type: point.supportType,
          connection_type: point.installationType,
          power_type: point.powerSupplyType,
          comment: point.comment || "",
          has_camera: point.cameras?.length > 0,
          ...(point.pointType === "high_point" && { coverage_area: point.coverageArea }),
          ...(point.pointType === "surveillance_center" && {
            recorder_size: point.recorderSize,
            screen_size: point.screenSize,
          }),
          localisations: apiLocalisations,
          cameras: point.cameras?.map((camera) => ({
            name: camera.name || "",
            role: camera.role || "",
            vision_area: camera.viewingArea || "",
            comment: camera.comment || "",
          })) || [],
        };

        await api.post(`/quotations/${quotationId}/point`, pointPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }

      setCollectedPoints([]);

      if (draftClickedRef.current) {
        navigate(`/invoice/create/step1/${quotationId}`);
      } else if (QuitClickedRef.current) {
        navigate("/");
      } else if (collectedPoints.length > 0 || points.length > 0) {
        navigate(`/invoice/create/step3/${quotationId}`);
      }
    } catch (error) {
      console.error("Error submitting points:", error);
      toast.error(
        `Failed to submit points: ${error.response?.data?.message || error.message || "Unexpected error"}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  const [showEditPoint, setShowEditPoint] = useState(false);
  const [editPointData, setEditPointData] = useState(null);

  // Function to handle editing an existing point
  const handleEditExistPoint = (index) => {
    const pointToEdit = points[index];
    const apiPointType = pointToEdit.type || pointToEdit.pointType;
    const dataForEdit = {
      id: pointToEdit.id,
      title: pointToEdit.title || pointToEdit.pointName,
      type: apiPointType,
      support_type: pointToEdit.support_type || pointToEdit.supportType,
      connection_type:
        pointToEdit.connection_type || pointToEdit.installationType,
      power_type: pointToEdit.power_type || pointToEdit.powerSupplyType,
      comment: pointToEdit.comment || "",
      has_camera: pointToEdit.has_camera,
      cameras: pointToEdit.cameras || [],
      localisations: pointToEdit.localisations || [],
      recorder_size: pointToEdit.recorder_size || pointToEdit.recorderSize,
      screen_size: pointToEdit.screen_size || pointToEdit.screenSize,
      coverage_area: pointToEdit.coverage_area || pointToEdit.coverageArea,
    };
    setEditPointData({
      pointToEdit: dataForEdit,
      quotationId: quotation?.id,
    });
    setShowEditPoint(true);
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ========== NEW EVENT HANDLERS ==========
  const handleEditPoint = (index) => {
    // const pointToEdit = collectedPoints[index];
    setShowViewpointFlow(true);
    setActiveDropdownIndex(index);
  };

  const handleDeletePoint = (index) => {
    setCollectedPoints((prev) => prev.filter((_, i) => i !== index));
    setActiveDropdownIndex(null);
  };

  const handleDeleteExistPoint = async (index) => {
    try {
      const quotationId = id; // from useParams()
      const token = localStorage.getItem("access_token");
      const pointId = points[index]?.id;

      if (!quotationId || !pointId || !token) {
        toast.error("Identifiants manquants pour la suppression.");
        return;
      }

      await api.post(
        `/quotations/${quotationId}/point/${pointId}/delete`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPoints((prev) => prev.filter((_, i) => i !== index));
      setActiveDropdownIndex(null);
      toast.success("Point supprimé avec succès.");
    } catch (error) {
      console.error("Error deleting point:", error);
      toast.error(
        `Échec de suppression: ${error.response?.data?.message || error.message || "Erreur inattendue"
        }`
      );
    }
  };

  const toggleDropdown = (index, type) => {
    setActiveDropdownIndex(activeDropdownIndex === index ? null : index);

    // Check if the clicked dropdown is already the active one
    if (activeDropdown && activeDropdown.type === type && activeDropdown.index === index) {
      setActiveDropdown(null); // If yes, close it
    } else {
      setActiveDropdown({ type, index }); // If no, open it
    }
  };

  const handleAddPoint = () => {
    setShowViewpointFlow(true);
  };

  const handleBackToAddPoint = (
    pointData,
    isEdit = false,
    editIndex = null
  ) => {
    setShowViewpointFlow(false);
    setQuickPointName(""); // Clear quick add name if used
    if (pointData) {
      const pointToAddOrUpdate = {
        ...pointData,
      };

      if (isEdit && editIndex !== null) {
        setCollectedPoints((prev) =>
          prev.map((p, i) => (i === editIndex ? pointToAddOrUpdate : p))
        );
      } else {
        setCollectedPoints((prev) => [...prev, pointToAddOrUpdate]);
      }
    }
    // Reset active dropdown if needed
    setActiveDropdownIndex(null);
  };

  const handleSaveDraftAndQuit = async () => {
    setIsSubmitting(true);
    try {
      const quotationId = window.location.pathname.split("/").pop();
      const token = localStorage.getItem("access_token");
      if (!quotationId || !token) throw new Error("Missing quotation ID or token.");

      for (const point of collectedPoints) {
        const manualCoords = parseCoordinates(point.gpsCoordinatesString || "");

        const photosData = point.photos
          ? (await Promise.all(point.photos.map(processPhoto))).filter(Boolean)
          : [];

        const apiLocalisations = buildLocalisations(manualCoords, photosData);

        const pointPayload = {
          title: point.pointName,
          type: point.pointType,
          support_type: point.supportType,
          connection_type: point.installationType,
          power_type: point.powerSupplyType,
          comment: point.comment || "",
          has_camera: point.cameras?.length > 0,
          ...(point.pointType === "high_point" && { coverage_area: point.coverageArea }),
          ...(point.pointType === "surveillance_center" && {
            recorder_size: point.recorderSize,
            screen_size: point.screenSize,
          }),
          localisations: apiLocalisations,
          cameras: point.cameras?.map((camera) => ({
            name: camera.name || "",
            role: camera.role || "",
            vision_area: camera.viewingArea || "",
            comment: camera.comment || "",
          })) || [],
        };

        await api.post(`/quotations/${quotationId}/point`, pointPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }
      setCollectedPoints([]);
      toast.success("Points enregistrés comme brouillon.");
      navigate(`/invoice/create/step1/${quotationId}`);
    } catch (error) {
      console.error("Error saving draft points:", error);
      toast.error(
        `Failed to save draft points: ${error.response?.data?.message || error.message || "Unexpected error"}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour gérer le clic sur le bouton retour
  const handleBackButtonClick = () => {
    // Afficher la modale dans tous les cas
    handleDraftClick();
    setShowBackModal(true);
  };

  const handleQuitButtonClick = () => {
    // Afficher la modale dans tous les cas
    handleQuitClick();
    setShowBackModal(true);
  };

  // ========== RENDER LOGIC ==========
  return (
    <HelmetProvider>
      {/* Global notification container */}
      <ToastContainer />
      {/* SEO Metadata */}
      <Helmet>
        <title>Créer un devis</title>
        <meta name="description" content="Formulaire de création de devis" />
      </Helmet>

      {/* Main content container */}
      <Container className="pt-4 d-flex justify-content-center">
        <div className="container-wrap w-100">
          {/* Conditional rendering based on flow state */}
          {!showViewpointFlow ? (
            <>
              {/* ========== HEADER SECTION ========== */}
              <div className="header d-flex  justify-content-between">
                <span>
                  <BackButton onClick={handleBackButtonClick} />
                </span>
                <span>Créer un devis</span>
                <img src="/close-btn.png" onClick={handleQuitButtonClick} />
              </div>

              {/* ========== STEP PROGRESS INDICATOR ========== */}
              <div className="step_bar d-flex justify-content-between align-items-center w-100 mb-4">
                <img
                  className="step-image img-fluid"
                  src="/step_done.png"
                  alt="step1"
                />
                <img
                  className="step-image img-fluid"
                  src="/step_done.png"
                  alt="step2"
                />
                <img
                  className="step-image img-fluid"
                  src="/step.png"
                  alt="step3"
                />
              </div>

              {/* ========== MAIN FORM CONTENT ========== */}
              <Form onSubmit={handleSubmit}>
                <p className="form-title mb-4">Configuration des points</p>
                {/* Add Point Button */}
                <div className="add-point-wrapper mb-4">
                  <Link
                    onClick={handleAddPoint}
                    className="add-point d-flex align-items-center"
                  >
                    <img src="/plus.png" alt="add point" className="me-0" />
                    Ajouter un point
                  </Link>
                </div>
                {/* ========== POINTS LIST SECTION ========== */}
                {collectedPoints.length > 0 && (
                  <div className="mb-3 points-list">
                    {collectedPoints.slice().reverse().map((point, index) => {
                      const Ooindex = collectedPoints.length - 1 - index;
                      const typeCounts = {};
                      collectedPoints.slice(0, index).forEach((p) => {
                        const type = getPointDisplayName(p.pointType);
                        typeCounts[type] = (typeCounts[type] || 0) + 1;
                      });
                      const currentTypeDisplayName = getPointDisplayName(
                        point.pointType
                      );
                      const count = (typeCounts[currentTypeDisplayName] || 0) + 1;

                      return (
                        <div
                          key={index}
                          className="point-item d-flex justify-content-between align-items-center"
                        >
                          <div className="point-info ">
                            <div className="point-type mb-2">
                              {currentTypeDisplayName}{" "}
                              {collectedPoints.filter(
                                (p) =>
                                  getPointDisplayName(p.pointType) ===
                                  currentTypeDisplayName
                              ).length > 1
                                ? count
                                : ""}
                            </div>
                            <div className="point-name">
                              {point.pointName ||
                                point.name ||
                                "Unnamed Point"}
                            </div>
                          </div>

                          <div className="photo-actions">
                            <span
                              className="photo-actions-icon"
                              alt="actions"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(Ooindex, 'collected');
                              }}
                            >
                              ⋮
                            </span>

                            {activeDropdown?.type === 'collected' && activeDropdown?.index === Ooindex && (
                              <div
                                className="custom-dropdown-menu"
                                ref={dropdownRef}
                              >
                                <button
                                  className="custom-dropdown-item"
                                  onClick={() => handleEditPoint(Ooindex)}
                                  type="button"
                                >
                                  <img
                                    src="/edit_alt.png"
                                    className="action-icon"
                                    alt="edit"
                                  ></img>{" "}
                                  Modifier
                                </button>
                                <button
                                  className="custom-dropdown-item"
                                  onClick={() => handleDeletePoint(Ooindex)}
                                  type="button"
                                >
                                  <img
                                    src="/close.png"
                                    className="action-icon"
                                    alt="close"
                                  ></img>{" "}
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}

                {loading ? (
                  <div className="contaner">
                    <Skeleton height={100} count={4} style={{ marginBottom: "1rem" }} />
                  </div>
                ) : (
                  <>
                    <div className="mb-4 points-list">
                      {points.slice().reverse().map((point, pointIndex) => {
                        const Oindex = points.length - 1 - pointIndex;
                        return (
                          <div
                            key={pointIndex}
                            className="point-item d-flex justify-content-between align-items-center"
                          >

                            <div className="point-info ">
                              <div className="point-type mb-2">{point.full_type || point.pointType}</div>
                              <div className="point-name">{point.title || point.pointName}</div>
                            </div>
                            <div className="photo-actions">
                              <span
                                className="photo-actions-icon"
                                alt="actions"
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDropdown(pointIndex, 'existing');
                                }}
                              >
                                ⋮
                              </span>
                              {activeDropdown?.type === 'existing' && activeDropdown?.index === pointIndex && (
                                <div
                                  className="custom-dropdown-menu"
                                  ref={dropdownRef}
                                >
                                  <button
                                    className="custom-dropdown-item"
                                    onClick={() => handleEditExistPoint(Oindex)}
                                    type="button"
                                  >
                                    <img
                                      src="/edit_alt.png"
                                      className="action-icon"
                                      alt="edit"
                                    ></img>{" "}
                                    Modifier
                                  </button>
                                  <button
                                    className="custom-dropdown-item"
                                    onClick={() => handleDeleteExistPoint(Oindex)}
                                    type="button"
                                  >
                                    <img
                                      src="/close.png"
                                      className="action-icon"
                                      alt="close"
                                    ></img>{" "}
                                    Supprimer
                                  </button>
                                </div>
                              )}
                            </div>

                          </div>
                        )
                      })}
                    </div>
                  </>
                )}
                {/* ========== FOOTER SECTION ========== */}
                <div className="footer shadow-lg d-flex justify-content-center">
                  <button
                    className="w-100 primary-button primary-button-block"
                    type="submit"
                    disabled={collectedPoints.length === 0 && points.length === 0 || isSubmitting}
                  >
                    {isSubmitting
                      ? "Envoi en cours..."
                      : "Voir le récapitulatif"}
                  </button>
                </div>
              </Form>
              {showEditPoint && (
                <div className="edit-point-overlay">
                  <EditPoint
                    pointToEdit={editPointData.pointToEdit}
                    quotationId={editPointData.quotationId}
                    onClose={() => setShowEditPoint(false)}
                  />
                </div>
              )}
            </>
          ) : (
            /* ========== VIEWPOINT FLOW SECTION ========== */
            <ViewpointFlow
              initialData={
                activeDropdownIndex !== null
                  ? {
                    ...collectedPoints[activeDropdownIndex],
                    // Ensure all form fields have values (not just empty strings)
                    supportType:
                      collectedPoints[activeDropdownIndex]?.supportType ||
                      null,
                    installationType:
                      collectedPoints[activeDropdownIndex]?.installationType ||
                      null,
                    powerSupplyType:
                      collectedPoints[activeDropdownIndex]?.powerSupplyType ||
                      null,
                    // Keep other fields as is
                    pointName:
                      collectedPoints[activeDropdownIndex]?.pointName || "",
                    pointType:
                      collectedPoints[activeDropdownIndex]?.pointType || "",
                    photos: collectedPoints[activeDropdownIndex]?.photos || [],
                    comment:
                      collectedPoints[activeDropdownIndex]?.comment || "",
                    hasCameras:
                      collectedPoints[activeDropdownIndex]?.hasCameras || "",
                    coverageArea:
                      collectedPoints[activeDropdownIndex]?.coverageArea || "",
                    recorderSize:
                      collectedPoints[activeDropdownIndex]?.recorderSize || "",
                    screenSize:
                      collectedPoints[activeDropdownIndex]?.screenSize || "",
                  }
                  : { pointName: quickPointName }
              }
              onComplete={(data) =>
                handleBackToAddPoint(
                  data,
                  activeDropdownIndex !== null,
                  activeDropdownIndex
                )
              }
              existingCameraCount={
                // Function to get voies count for a camera type
                (() => {
                  const getCameraVoiesCount = (cameraRole) => {
                    const voiesMap = {
                      'Environnementale': 1,
                      'Environnementale Acupik': 1,
                      'Longue Porté': 1,
                      'VPI': 1,
                      'Multi-capteurs': 4,
                      '180° + Dome motorisé': 2,
                      'Dome motorisé X25': 1,
                      'Dome motorisé X45': 1,
                      'Dome motorisé Longue porté': 1,
                      '360° + Dome motorisé': 2
                    };
                    return voiesMap[cameraRole] || 1; // Default to 1 if not found
                  };

                  // Sum voies from already saved API points
                  const apiPointsVoies = (points || []).reduce((sum, p) => {
                    if (Array.isArray(p?.cameras)) {
                      return sum + p.cameras.reduce((cameraSum, camera) => {
                        return cameraSum + getCameraVoiesCount(camera.role);
                      }, 0);
                    }
                    return sum;
                  }, 0);

                  // Sum voies from locally collected points
                  const collectedPointsVoies = (collectedPoints || []).reduce((sum, p) => {
                    if (Array.isArray(p?.cameras)) {
                      return sum + p.cameras.reduce((cameraSum, camera) => {
                        return cameraSum + getCameraVoiesCount(camera.role);
                      }, 0);
                    }
                    return sum;
                  }, 0);

                  return apiPointsVoies + collectedPointsVoies;
                })()
              }
            />
          )}
        </div>
      </Container>

      {/* ========== CONFIRMATION MODAL SECTION ========== */}
      {/* Modale de confirmation pour le bouton retour */}
      <ConfirmationModal
        show={showBackModal}
        handleClose={() => setShowBackModal(false)}
        handleConfirm={() => {
          if (draftClickedRef.current) {
            handleSaveDraftAndQuit();
          } else if (QuitClickedRef.current) {
            navigate("/");
          }
        }}
        title="Vous n'avez pas terminé de compléter votre devis"
        isSubmitting={isSubmitting}
        buttonTitle="Enregistrer comme brouillon"
        LinkTitle="Quitter sans enregistrer"
      >
        <p className="text-center">
          Vous êtes sur le point de quitter sans avoir terminé votre devis.
          Êtes-vous sûr de vouloir quitter ? <br />
          Sinon, vous pouvez l'enregistrer en brouillon pour le retrouver plus
          tard.
        </p>
      </ConfirmationModal>

    </HelmetProvider>
  );
};

export default PointsInfo;


const getPointDisplayName = (pointType) => {
  switch (pointType) {
    case "vision_point":
      return "Point de vision";
    case "relay_point":
      return "Point relais";
    case "high_point":
      return "Point haut";
    case "surveillance_center":
      return "Centre de surveillance";
    default:
      return "Unknown Type";
  }
};
